import { faMoneyBill as fasMoneyBill } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import type { CommonOptions } from "bm-ui-kit";
import compact from "lodash/compact";
import React from "react";
import type { ReactNode } from "react";
import { Form } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import type { FormMaskedInputProps } from "./FormMaskedInput";
import { FormMaskedInput } from "./FormMaskedInput";

interface FormCurrencyProps extends FormMaskedInputProps<number> {
	min?: number;
	max?: number;
	step?: number;
}

export class FormCurrency extends FormMaskedInput<number, FormCurrencyProps> {
	public static defaultProps: Partial<FormCurrencyProps> = {
		append: "Ft",
		type: "tel",
		prepend: <FontAwesomeIcon icon={fasMoneyBill} />,
	};

	protected format(amount: number): string {
		if (!amount && amount !== 0) {
			return "";
		}

		if (amount === Number.NEGATIVE_INFINITY) {
			return "-";
		}

		// Ezres tagolás
		return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	}

	protected parse(text: string): number {
		if (text === "-") {
			return Number.NEGATIVE_INFINITY;
		}

		if (typeof text !== "string") {
			return text;
		}

		const cleanText = text.replace(/[^0-9-]+/g, "");

		if (cleanText && cleanText.match(/^0{2,}$/)) {
			return cleanText as unknown as number;
		}

		if (!cleanText || isNaN(Number(cleanText))) {
			return undefined;
		}

		return Number(cleanText);
	}

	protected renderInput(fieldProps: FieldRenderProps<number>, _additionalClass?: string): ReactNode {
		const {
			id,
			disabled,
			size,
			placeholder = "Válassz a listából!",
			required,
			readOnly,
			tabIndex,
			min,
			max,
			step,
			dataList,
		} = this.props;
		const {
			input: { name, value },
			meta: { invalid, touched },
		} = fieldProps;

		if (dataList || (min && max && step)) {
			const itemId = id || name + this.id;

			const options: CommonOptions<number> = [];

			if (dataList) {
				dataList.forEach((i) =>
					options.push({
						label: formatText(i, "ft").replace(" Ft", ""),
						value: i,
					}),
				);
			} else {
				for (let i = min; i <= max; i += step) {
					options.push({
						label: formatText(i, "ft").replace(" Ft", ""),
						value: i,
					});
				}
			}

			const className = compact([
				"custom-select",
				placeholder && "has-placeholder",
				placeholder && value === undefined && "placeholder",
				"d-md-none",
			]).join(" ");

			return (
				<>
					{super.renderInput(fieldProps, "d-none d-md-block")}
					<Form.Control
						id={`${itemId}-md`}
						name={name}
						className={className}
						as="select"
						size={size}
						readOnly={readOnly}
						disabled={disabled || readOnly}
						tabIndex={tabIndex}
						required={required}
						// isValid={!disabled && touched && valid}
						isInvalid={!disabled && touched && invalid}
						value={String(value) as unknown as string}
						onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
							const element = event.currentTarget;
							const index = placeholder ? element.selectedIndex - 1 : element.selectedIndex;
							const newValue = options[index].value;

							this.handleChange(newValue, fieldProps);
						}}
						onFocus={(event: React.FocusEvent<HTMLSelectElement>) => {
							this.handleFocus(event, fieldProps);
						}}
						onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
							this.handleBlur(event, fieldProps);
						}}
					>
						{placeholder && (
							<option value="" disabled={value !== undefined}>
								{placeholder}
							</option>
						)}
						{options.map((option) => (
							<option key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
								{option.label}
							</option>
						))}
					</Form.Control>
				</>
			);
		}

		return super.renderInput(fieldProps);
	}

	@autobind
	protected handleValidate(value: number, allValues: Record<string, any>): string {
		if (String(value).match(/^0{2,}$/)) {
			return "Érvénytelen érték!";
		}

		return super.handleValidate(String(value) === "" || value === undefined ? NaN : value, allValues);
	}

	protected renderDataList(_dataListId: string, _dataList: number[], _value: number): ReactNode {
		// Csak mobilon akarjuk, és ott a sima select option-ok is ugyanúgy ellátják ezt a funkciót
		return null;
	}

	public static generateDataList(min: number, max: number, step: number): number[] {
		const dataList = [];

		for (let i = min; i <= max; i += step) {
			dataList.push(i);
		}

		return dataList;
	}
}
